// src/utils/markdownBlocks.js
//
// Pure block parser behind components/AnswerMarkdown.jsx (docs/sdlc/investigating-analyst, Task 11,
// ACs 14, 66, 67). No dependency, no DOM, never throws. Output is plain data; the component turns it
// into React elements, so no string here is ever treated as HTML (AC 68).
//
// Blocks: { type:'h1'|'h2'|'h3', text } | { type:'p', text } | { type:'ul'|'ol', items:[] }
//       | { type:'table', header:[], align:[], rows:[[]] } | { type:'code', lang, text }
// Kept from the original AnswerMarkdown parser: '##'/'###' headings, bullet/numbered lists, paragraphs
// (consecutive lines joined with a space). Added: '#' headings, pipe tables, fenced code.

const FENCE_RE = /^\s*```\s*([\w+-]*)\s*$/;
const SEP_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

// Split a table row on unescaped pipes; drops the empty edge cells made by leading/trailing pipes.
export function splitRow(line) {
  const s = String(line ?? '').trim();
  const cells = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '\\' && s[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (ch === '|') { cells.push(cur); cur = ''; continue; }
    cur += ch;
  }
  cells.push(cur);
  if (s.startsWith('|')) cells.shift();
  if (s.endsWith('|') && !s.endsWith('\\|')) cells.pop();
  return cells.map((c) => c.trim());
}

function isSeparator(line) {
  return typeof line === 'string' && line.includes('|') && line.includes('-') && SEP_RE.test(line);
}

function alignOf(cell) {
  const l = cell.startsWith(':');
  const r = cell.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  return 'left';
}

export function parseMarkdownBlocks(text) {
  try {
    const lines = String(text ?? '').split('\n');
    const blocks = [];
    let para = [];
    let list = null;
    const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join(' ').trim() }); para = []; } };
    const flushList = () => { if (list && list.items.length) blocks.push(list); list = null; };

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i].trimEnd();
      if (!line.trim()) { flushPara(); flushList(); continue; }

      const fence = FENCE_RE.exec(line);
      if (fence) {
        flushPara(); flushList();
        const body = [];
        i += 1;
        while (i < lines.length && !FENCE_RE.test(lines[i].trimEnd())) { body.push(lines[i].replace(/\r$/, '')); i += 1; }
        // an unterminated fence consumes to the end; a closing fence is skipped by the loop's i += 1
        blocks.push({ type: 'code', lang: fence[1] || '', text: body.join('\n') });
        continue;
      }

      if (line.includes('|') && isSeparator(lines[i + 1])) {
        const header = splitRow(line);
        const sep = splitRow(lines[i + 1]);
        if (header.length && header.length === sep.length) {
          flushPara(); flushList();
          const rows = [];
          i += 2;
          while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
            const cells = splitRow(lines[i]);
            while (cells.length < header.length) cells.push('');
            rows.push(cells.slice(0, header.length));
            i += 1;
          }
          i -= 1; // the for loop advances past the last consumed line
          blocks.push({ type: 'table', header, align: sep.map(alignOf), rows });
          continue;
        }
      }

      const h3 = line.match(/^###\s+(.*)/);
      const h2 = line.match(/^##\s+(.*)/);
      const h1 = line.match(/^#\s+(.*)/);
      const ul = line.match(/^[-*]\s+(.*)/);
      const ol = line.match(/^\d+\.\s+(.*)/);
      if (h3) { flushPara(); flushList(); blocks.push({ type: 'h3', text: h3[1] }); continue; }
      if (h2) { flushPara(); flushList(); blocks.push({ type: 'h2', text: h2[1] }); continue; }
      if (h1) { flushPara(); flushList(); blocks.push({ type: 'h1', text: h1[1] }); continue; }
      if (ul) {
        flushPara();
        if (!list || list.type !== 'ul') { flushList(); list = { type: 'ul', items: [] }; }
        list.items.push(ul[1]);
        continue;
      }
      if (ol) {
        flushPara();
        if (!list || list.type !== 'ol') { flushList(); list = { type: 'ol', items: [] }; }
        list.items.push(ol[1]);
        continue;
      }
      flushList();
      para.push(line.trim());
    }
    flushPara();
    flushList();
    return blocks;
  } catch {
    return [];
  }
}
